import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import Logo from './Logo';

const YEAR = new Date().getFullYear();

const LINKS = [
  {
    title: 'Продукт',
    items: [
      { label: 'Касса для iPad', href: '/#products' },
      { label: 'Админка в телефоне', href: '/#admin-mobile' },
      { label: 'Электронное меню', href: '/#menu' },
      { label: 'Веб-админка', href: '/#features' },
      { label: 'Загрузки', href: '/downloads', internal: true },
    ],
  },
  {
    title: 'Компания',
    items: [
      { label: 'Цена', href: '/#pricing' },
      { label: 'Вопросы', href: '/#faq' },
      { label: 'Документация', href: '/docs', internal: true },
      { label: 'Поддержка', href: '/support', internal: true },
      { label: 'Контакты', href: '/contacts', internal: true },
      { label: 'Безопасность', href: '/legal/security', internal: true },
    ],
  },
  {
    title: 'Документы',
    items: [
      { label: 'Оферта', href: '/legal/offer', internal: true },
      { label: 'Политика конфиденциальности', href: '/legal/privacy', internal: true },
      { label: 'Согласие на обработку', href: '/legal/consent', internal: true },
      { label: 'Cookies', href: '/legal/cookies', internal: true },
      { label: 'Возвраты', href: '/legal/return', internal: true },
    ],
  },
];

function TelegramIcon({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M21.94 4.5 18.6 20.55c-.25 1.13-.91 1.41-1.84.88l-5.09-3.75-2.46 2.36c-.27.27-.5.5-1.03.5l.37-5.18 9.43-8.51c.41-.36-.09-.57-.63-.21L6.7 13.27 1.69 11.7c-1.09-.34-1.11-1.09.23-1.62L20.5 2.92c.91-.34 1.71.21 1.44 1.58z" />
    </svg>
  );
}

function WhatsappIcon({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.556-5.338 11.891-11.893 11.891a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.695.247-1.29.173-1.414z" />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer className="section-divider bg-white">
      <div className="container-site grid gap-12 py-16 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Logo />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-ink/60">
            Облачная система управления рестораном. Админ-панель, касса и&nbsp;электронное меню
            в&nbsp;одной подписке.
          </p>

          <ul className="mt-6 space-y-2 text-[13px] text-ink/65">
            <li className="flex items-start gap-2.5">
              <Phone size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink/45" />
              <a href="tel:+79894734322" className="hover:text-ink transition-colors">
                +7 989 473-43-22
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink/45" />
              <span className="flex flex-col gap-1">
                <a href="mailto:resstrosaas@mail.ru" className="hover:text-ink transition-colors">
                  resstrosaas@mail.ru
                </a>
                <a href="mailto:admin@resstro.ru" className="hover:text-ink transition-colors">
                  admin@resstro.ru
                </a>
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin size={14} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink/45" />
              <span>Россия, для всех регионов</span>
            </li>
          </ul>

          <div className="mt-6 flex items-center gap-2">
            <a
              href="https://wa.me/79894734322"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="grid h-10 w-10 place-items-center rounded-full bg-paper text-ink/65 transition-colors hover:bg-ink hover:text-white"
            >
              <WhatsappIcon size={14} />
            </a>
            <a
              href="https://t.me/resstrosaas"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Telegram"
              className="grid h-10 w-10 place-items-center rounded-full bg-paper text-ink/65 transition-colors hover:bg-ink hover:text-white"
            >
              <TelegramIcon size={14} />
            </a>
          </div>
        </div>

        {LINKS.map((group) => (
          <div key={group.title}>
            <h4 className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
              {group.title}
            </h4>
            <ul className="mt-5 space-y-3">
              {group.items.map((i) => (
                <li key={i.label}>
                  {'internal' in i && i.internal ? (
                    <Link to={i.href} className="text-sm text-ink/80 hover:text-ink transition">
                      {i.label}
                    </Link>
                  ) : (
                    <a href={i.href} className="text-sm text-ink/80 hover:text-ink transition">
                      {i.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Платёжные партнёры: текстом, без чужих логотипов. */}
      <div className="section-divider">
        <div className="container-site py-8">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/45">
            Платежи и инфраструктура
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] font-semibold text-ink/55">
            <span>ЮKassa</span>
            <span>СБП</span>
            <span>Mir</span>
            <span>Visa</span>
            <span>Mastercard</span>
            <span>OFD.ru</span>
          </div>
        </div>
      </div>

      <div className="section-divider">
        <div className="container-site flex flex-col items-start justify-between gap-3 py-6 text-xs text-ink/50 sm:flex-row sm:items-center">
          {/* Год вычисляется при пререндере на сервере; после Нового года до пересборки
              он отличается от клиентского. Это ожидаемо, не ошибка гидрации. */}
          <span suppressHydrationWarning>
            © {YEAR} Resstro SaaS · ИП Раджабов Рустам Эфлетдинович · ИНН&nbsp;
            <span className="tabular-nums">051707862851</span>
          </span>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() =>
                window.dispatchEvent(new Event('resstro:open-cookie-settings'))
              }
              className="hover:text-ink transition-colors"
            >
              Настроить cookies
            </button>
            <span className="hidden sm:inline">·</span>
            <span>Сделано в России · Серверы в России</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
